import type {
  Memo,
  Session,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  ApiError,
  AiSummaryResponse,
  Summary,
  SummariesPullResponse,
  SummariesPushRequest,
  SummariesPushResponse,
} from './types.js';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  baseUrl: string;
  fetch: FetchLike;
  /** Returns the bearer token if the user is authenticated, else null. */
  getToken: () => string | null;
}

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly apiError: ApiError,
  ) {
    super(apiError.message);
    this.name = 'ApiClientError';
  }
}

export class ApiClient {
  constructor(private opts: ApiClientOptions) {}

  /** Returns the JSON body, throwing ApiClientError on non-2xx (304 returns undefined). */
  private async req<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('content-type', 'application/json');
    const token = this.opts.getToken();
    if (token) headers.set('authorization', `Bearer ${token}`);

    const res = await this.opts.fetch(`${this.opts.baseUrl}${path}`, { ...init, headers });
    if (res.status === 304) return undefined as T;
    const text = await res.text();
    const body = text ? JSON.parse(text) : undefined;
    if (!res.ok) {
      const err = (body ?? {}) as Partial<ApiError>;
      const apiError: ApiError = {
        code: err.code ?? `HTTP_${res.status}`,
        message: err.message ?? res.statusText,
      };
      throw new ApiClientError(res.status, apiError);
    }
    return body as T;
  }

  // ----- Auth -----
  // 注: Google OAuth のフローは
  //   1. POST /api/auth/sign-in/social {provider:'google', callbackURL} → Better-Auth が
  //      Google 認可 URL をレスポンス {url, redirect:true} で返す
  //   2. PWA は受け取った url に window.location で遷移 → Google 認証
  //   3. Google が /api/auth/callback/google にリダイレクト → Better-Auth がセッション作成し
  //      callbackURL (PWA の /auth/callback) にリダイレクト (Bearer プラグインがレスポンスに
  //      `set-auth-token` ヘッダで token を載せる)
  //   4. PWA は /auth/callback 側で getSession() を呼んで token を取得 → localStorage 保存

  async signInGoogle(callbackURL: string): Promise<{ url: string; redirect: boolean }> {
    return this.req('/api/auth/sign-in/social', {
      method: 'POST',
      body: JSON.stringify({ provider: 'google', callbackURL }),
    });
  }

  async getSession(): Promise<Session | null> {
    try {
      // Better-Auth の `/api/auth/get-session` は { user, session } 形式で返す。
      // Bearer プラグインを有効化していれば、レスポンスヘッダ `set-auth-token` か
      // ボディの `session.token` から token を読み取れる。実装側で適切にマップする。
      const raw = await this.req<{
        user: { id: string; email: string; emailVerified: boolean; plan?: 'free' | 'pro'; createdAt: string };
        session: { token: string; expiresAt: string };
      } | null>('/api/auth/get-session');
      if (!raw) return null;
      return {
        token: raw.session.token,
        expiresAt: raw.session.expiresAt,
        user: {
          id: raw.user.id,
          email: raw.user.email,
          emailVerified: raw.user.emailVerified,
          plan: raw.user.plan ?? 'free',
          createdAt: raw.user.createdAt,
        },
      };
    } catch (e: unknown) {
      if (e instanceof ApiClientError && e.status === 401) return null;
      throw e;
    }
  }

  async signOut(): Promise<void> {
    await this.req('/api/auth/sign-out', { method: 'POST' });
  }

  // ----- Sync -----
  async pull(since: string | null, limit = 100): Promise<SyncPullResponse> {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    params.set('limit', String(limit));
    const result = await this.req<SyncPullResponse | undefined>(`/api/sync/pull?${params.toString()}`);
    // 304 Not Modified: 新着なし。空ページとして扱い、cursor は据え置き
    return result ?? { memos: [], nextCursor: null, serverTime: since ?? new Date().toISOString() };
  }

  async push(memos: Memo[]): Promise<SyncPushResponse> {
    const body: SyncPushRequest = { memos };
    return this.req('/api/sync/push', { method: 'POST', body: JSON.stringify(body) });
  }

  // ----- AI -----
  async summarize(date: string): Promise<Summary> {
    return this.req('/api/ai/summarize', {
      method: 'POST',
      body: JSON.stringify({ date }),
    });
  }

  // ----- Summaries -----
  async getSummary(date: string): Promise<Summary | null> {
    return this.req(`/api/summaries/${date}`);
  }

  async pushSummaries(summaries: Summary[]): Promise<SummariesPushResponse> {
    const body: SummariesPushRequest = { summaries };
    return this.req('/api/sync/summaries/push', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async pullSummaries(since: string | null, limit = 100): Promise<SummariesPullResponse> {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    params.set('limit', String(limit));
    const result = await this.req<SummariesPullResponse | undefined>(
      `/api/sync/summaries/pull?${params.toString()}`,
    );
    return result ?? { summaries: [], nextCursor: null, serverTime: since ?? new Date().toISOString() };
  }
}
